import React, { useEffect, useRef } from 'react';
import { AppProps } from 'next/app';
import Head from 'next/head';
import TagManager from 'react-gtm-module';
import * as CookieConsent from 'vanilla-cookieconsent';
import { observer } from 'mobx-react-lite';
import { IS_MAINTENANCE, StatusMessageEnum } from '@app/common';
import { WebSeo } from '../components/web/seo/web-seo';
import { CookieService } from '../models/services/cookie-service';
import '../../public/fonts/fonts.css';
import '../styles/globals.css';
import '../../public/html/style.css';
import '../components/web/page-transitions/page-transitions.css';
import { PageTransitions } from '../components/web/page-transitions/page-transitions';
import '../translation/i18n';
import { ThemeProvider } from '../providers/theme-provider';
import { RootStoreProvider, useRootStore } from '../providers/root-store-provider';
import 'vanilla-cookieconsent/dist/cookieconsent.css';
import '../../public/cookie/cookie.css';
import useCookies from '../hooks/use-cookies';
import { Maintenance } from './app/maintenance';
import { RootStoreHydration } from '../models/stores/root-store';
import { Onboarding } from '../components/app/onboarding/onboarding';
import { APP_STORE_APP_ID } from '../config/app-store';
import { PricingOfferProvider } from '../providers/pricing-offer-provider';
import {
    captureAdsMeasurementContextResult,
    isAdsMeasurementEnabled,
    pollLateAdsMeasurementContext,
    readStoredAdsConsentState,
    revokeAdsMeasurementContext,
    shouldSkipExternalConsentSync,
    shouldSyncAdsMeasurementConsent,
    settleAdsMeasurementSync,
    syncAdsMeasurementContext,
    writeStoredAdsConsentState,
} from '../models/functions/ads-measurement-context';
import {
    pushConsentModeDefault,
    pushConsentModeUpdate,
    shouldInitializeTagManager,
    shouldReloadAfterOptionalConsentRevocation,
} from '../utils/data-layer';

const tagManagerArgs = {
    gtmId: 'GTM-TG5Q4R6',
};

const adsMeasurementEnabled = isAdsMeasurementEnabled(process.env.NEXT_PUBLIC_ADS_CONVERSIONS_ENABLED);

interface AdsMeasurementLifecycleProps {
    advertisingConsent: boolean;
    consentReady: boolean;
    consentSyncSource: 'initial' | 'local' | 'external';
}

const AdsMeasurementLifecycle: React.FC<AdsMeasurementLifecycleProps> = observer(
    ({ advertisingConsent, consentReady, consentSyncSource }) => {
        const rootStore = useRootStore();
        const userId = rootStore.authStore.userProfile?.id;
        const previousUserId = useRef<string>();

        useEffect(() => {
            let active = true;
            const cleanup = () => {
                active = false;
            };
            if (!consentReady) return cleanup;
            const userIdBeforeConsentEffect = previousUserId.current;
            previousUserId.current = userId;

            if (!adsMeasurementEnabled || !advertisingConsent) {
                revokeAdsMeasurementContext();
            }

            const capture =
                adsMeasurementEnabled && advertisingConsent
                    ? captureAdsMeasurementContextResult(true)
                    : { changed: false, context: null, explicitClick: false };
            if (adsMeasurementEnabled && advertisingConsent) {
                pollLateAdsMeasurementContext({
                    capture: () => captureAdsMeasurementContextResult(true),
                    isActive: () => active,
                    wait: (milliseconds) =>
                        new Promise((resolve) => {
                            window.setTimeout(resolve, milliseconds);
                        }),
                });
            }
            const { context } = capture;
            if (
                !userId ||
                shouldSkipExternalConsentSync(consentSyncSource, userId, userIdBeforeConsentEffect) ||
                !shouldSyncAdsMeasurementConsent(adsMeasurementEnabled, advertisingConsent)
            ) {
                return cleanup;
            }

            const storedState = readStoredAdsConsentState(userId);
            if (storedState?.advertisingConsent === advertisingConsent && !capture.explicitClick) return cleanup;

            settleAdsMeasurementSync({
                isActive: () => active,
                onStale: (state) => {
                    writeStoredAdsConsentState(userId, state);
                    if (advertisingConsent) {
                        revokeAdsMeasurementContext();
                        const { categories } = CookieConsent.getCookie();
                        CookieConsent.acceptCategory(
                            Array.isArray(categories)
                                ? categories.filter((category) => category !== 'advertising')
                                : [],
                        );
                    }
                },
                onSuccess: (state) => writeStoredAdsConsentState(userId, state),
                request: () =>
                    syncAdsMeasurementContext(
                        (input) =>
                            rootStore.apiClient.userController.userControllerUpdateAdsMeasurement({
                                updateAdsMeasurementDto: {
                                    ...input,
                                    capturedAt: input.capturedAt ? new Date(input.capturedAt) : undefined,
                                },
                            }),
                        context,
                        storedState?.generation ?? 0,
                        advertisingConsent,
                    ),
            });

            return cleanup;
        }, [advertisingConsent, consentReady, consentSyncSource, rootStore.apiClient.userController, userId]);

        return null;
    },
);

type MyAppProps = AppProps<{ initialData: RootStoreHydration }> & {
    host: string;
};

function MyApp({ Component, router, pageProps }: MyAppProps): React.ReactElement {
    const host = process.env.NEXT_PUBLIC_API_URL;
    if (!host) {
        throw new Error(StatusMessageEnum.EnvApiUrlUndefined);
    }
    const cookieService = new CookieService(undefined, pageProps.initialData?.availablePremiumLookupKeys);

    const isApp = router.pathname.includes('/app');
    const tagManagerInitialized = useRef(false);
    const consentModeInitialized = useRef(false);
    const previousAdvertisingConsent = useRef(false);
    const previousAnalyticsConsent = useRef(false);

    const { advertisingCookieAccepted, analyticsCookieAccepted, cookieConsentReady, consentSyncSource } =
        useCookies(isApp);

    useEffect(() => {
        if (!adsMeasurementEnabled) return;
        if (!consentModeInitialized.current) {
            pushConsentModeDefault();
            consentModeInitialized.current = true;
        }
        pushConsentModeUpdate({
            advertisingConsent: advertisingCookieAccepted,
            analyticsConsent: analyticsCookieAccepted,
        });
    }, [advertisingCookieAccepted, analyticsCookieAccepted]);

    useEffect(() => {
        if (
            !tagManagerInitialized.current &&
            shouldInitializeTagManager({
                adsMeasurementEnabled,
                advertisingConsent: advertisingCookieAccepted,
                analyticsConsent: analyticsCookieAccepted,
                authenticated: cookieService.hasJwtToken(),
            })
        ) {
            TagManager.initialize(tagManagerArgs);
            tagManagerInitialized.current = true;
        }
    }, [router.route, advertisingCookieAccepted, analyticsCookieAccepted]);

    useEffect(() => {
        if (
            shouldReloadAfterOptionalConsentRevocation({
                adsMeasurementEnabled,
                advertisingConsent: advertisingCookieAccepted,
                analyticsConsent: analyticsCookieAccepted,
                previousAdvertisingConsent: previousAdvertisingConsent.current,
                previousAnalyticsConsent: previousAnalyticsConsent.current,
                tagManagerInitialized: tagManagerInitialized.current,
            })
        ) {
            window.location.reload();
            return;
        }
        previousAdvertisingConsent.current = advertisingCookieAccepted;
        previousAnalyticsConsent.current = analyticsCookieAccepted;
    }, [advertisingCookieAccepted, analyticsCookieAccepted]);

    if (IS_MAINTENANCE) {
        return (
            <RootStoreProvider
                host={host}
                cookieService={cookieService}
                initialData={pageProps.initialData}
                isSSR={!isApp}
            >
                <PricingOfferProvider lookupKeys={pageProps.initialData?.availablePremiumLookupKeys}>
                    <ThemeProvider>
                        <Maintenance />
                    </ThemeProvider>
                </PricingOfferProvider>
            </RootStoreProvider>
        );
    }

    return (
        <>
            <PageTransitions />
            <Head>
                <meta name="robots" content="noindex, follow" key="robots" />
                <meta name="viewport" content="width=device-width, initial-scale=1" />
                <meta name="apple-itunes-app" content={`app-id=${APP_STORE_APP_ID}`} />
                <link rel="stylesheet" href="https://fonts.googleapis.com/icon?family=Material+Icons" />
                <link rel="apple-touch-icon" sizes="180x180" href="/images/favicon/apple-touch-icon.png" />
                <link rel="icon" type="image/png" sizes="32x32" href="/images/favicon/favicon-32x32.png" />
                <link rel="icon" type="image/png" sizes="16x16" href="/images/favicon/favicon-16x16.png" />
                <link rel="manifest" href="/images/favicon/site.webmanifest" />
                <link rel="mask-icon" href="/images/favicon/safari-pinned-tab.svg" color="#231d61" />
                <link rel="shortcut icon" href="/images/favicon/favicon.ico" />
                <meta name="msapplication-TileColor" content="#da532c" />
                <meta name="msapplication-config" content="/images/favicon/browserconfig.xml" />
                <meta name="theme-color" content="#ffffff" media="(prefers-color-scheme: light)" />
                <meta name="theme-color" content="#1A1A29" media="(prefers-color-scheme: dark)" />
                {/* <meta property="fb:page_id" content="275957925840243" /> */}
            </Head>
            <WebSeo />

            <RootStoreProvider
                host={host}
                cookieService={cookieService}
                initialData={pageProps.initialData}
                isSSR={!isApp}
            >
                <PricingOfferProvider lookupKeys={pageProps.initialData?.availablePremiumLookupKeys}>
                    <ThemeProvider>
                        <AdsMeasurementLifecycle
                            advertisingConsent={advertisingCookieAccepted}
                            consentReady={cookieConsentReady}
                            consentSyncSource={consentSyncSource}
                        />
                        <Component {...pageProps} router={router} />
                        <Onboarding />
                    </ThemeProvider>
                </PricingOfferProvider>
            </RootStoreProvider>
        </>
    );
}

export default MyApp;
